'use client';

import { c as _c } from "react/compiler-runtime";
import { Divider } from '@xefi/x-react/divider';
import dayjs from 'dayjs';
import type { FormikProps } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Entry, SurveyTaking } from '@/models/survey';
import type { SurveyTakingFormValues } from '../../[surveyId]/_components/SurveyTaking';
import { ExpiredTimerModal } from './ExpiredTimerModal';
type SurveyTimerProps = {
  currentPage: number;
  currentSurvey: SurveyTaking;
  formik?: FormikProps<SurveyTakingFormValues>;
  handleSubmitAnswers?: (values: SurveyTakingFormValues) => void;
  entry?: Entry;
  isPreview?: boolean;
  stopTimer: boolean;
};
const parseTimerToSeconds = (timer: number | string | undefined): number => {
  if (timer == null) return 0;
  if (typeof timer === 'number') {
    return Math.max(0, Math.floor(timer * 60));
  }
  const parts = timer.split(':').map(segment => {
    const value = parseFloat(segment);
    return Number.isFinite(value) ? value : 0;
  });
  if (parts.length === 0) return 0;
  const length = parts.length;
  const seconds = (parts[length - 1] ?? 0) + (parts[length - 2] ?? 0) * 60 + (parts[length - 3] ?? 0) * 3600;
  return Math.max(0, Math.floor(seconds));
};
export const SurveyTimer = t0 => {
  const $ = _c(47);
  const {
    currentPage,
    currentSurvey,
    formik,
    handleSubmitAnswers,
    entry,
    isPreview,
    stopTimer
  } = t0;
  currentSurvey?.timer;
  entry?.started_at;
  let t1;
  bb0: {
    const timer = currentSurvey?.timer;
    const totalSeconds = parseTimerToSeconds(timer ?? 0);
    const startedAtStr = entry?.started_at;
    if (!startedAtStr) {
      t1 = totalSeconds;
      break bb0;
    }
    const nowMs = Date.now();
    const cleanedForDateParse = _temp;
    let startedAtMs;
    try {
      startedAtMs = Date.parse(cleanedForDateParse(startedAtStr));
    } catch {
      startedAtMs = Number.NaN;
    }
    if (!Number.isNaN(startedAtMs) && startedAtMs <= nowMs) {
      const elapsedSeconds = Math.floor((nowMs - startedAtMs) / 1000);
      t1 = Math.max(0, totalSeconds - elapsedSeconds);
      break bb0;
    }
    const asLocalCandidate = (() => {
      const withoutTimezone = startedAtStr.replace(/Z$/, "");
      const fractionalMatch = withoutTimezone.match(/\.(\d+)/);
      let localCandidate = withoutTimezone;
      if (fractionalMatch) {
        const milliseconds_0 = fractionalMatch[1].slice(0, 3).padEnd(3, "0");
        localCandidate = withoutTimezone.replace(/\.\d+$/, `.${milliseconds_0}`);
      }
      return localCandidate;
    })();
    let startedAtLocalMs;
    try {
      startedAtLocalMs = Date.parse(asLocalCandidate);
    } catch {
      startedAtLocalMs = Number.NaN;
    }
    if (!Number.isNaN(startedAtLocalMs) && startedAtLocalMs <= nowMs) {
      const elapsedSeconds_0 = Math.floor((nowMs - startedAtLocalMs) / 1000);
      t1 = Math.max(0, totalSeconds - elapsedSeconds_0);
      break bb0;
    }
    if ($[0] !== startedAtStr || $[1] !== totalSeconds) {
      const startedAt = dayjs(startedAtStr);
      if (startedAt.isValid()) {
        const elapsed = Math.floor((nowMs - startedAt.valueOf()) / 1000);
        if (elapsed > 0) {
          t1 = Math.max(0, totalSeconds - elapsed);
          break bb0;
        }
      }
      $[0] = startedAtStr;
      $[1] = totalSeconds;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    t1 = totalSeconds;
  }
  const initialSeconds = t1;
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false);
  const isSubmittedRef = useRef(false);
  const latestValuesRef = useRef(formik?.values);
  const timerIdRef = useRef(null);
  const isRunning = currentPage > 0 || !!entry?.started_at;
  const formatSecondsToHHMMSS = _temp2;
  let t2;
  if ($[3] !== formik?.values) {
    t2 = () => {
      latestValuesRef.current = formik?.values;
    };
    $[3] = formik?.values;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = formik?.values;
  let t4;
  if ($[5] !== t3) {
    t4 = [t3];
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t2, t4);
  let t5;
  let t6;
  if ($[7] !== initialSeconds || $[8] !== stopTimer) {
    t5 = () => {
      if (stopTimer) {
        return;
      }
      setSecondsLeft(initialSeconds);
      isSubmittedRef.current = false;
    };
    t6 = [initialSeconds, stopTimer];
    $[7] = initialSeconds;
    $[8] = stopTimer;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[11] !== isRunning || $[12] !== stopTimer) {
    t7 = () => {
      if (!isRunning || stopTimer) {
        if (timerIdRef.current) {
          clearInterval(timerIdRef.current);
          timerIdRef.current = null;
        }
        return;
      }
      timerIdRef.current = window.setInterval(() => {
        setSecondsLeft(seconds_0 => {
          if (seconds_0 <= 1) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = null;
            return 0;
          }
          return seconds_0 - 1;
        });
      }, 1000);
      return () => {
        if (timerIdRef.current) {
          clearInterval(timerIdRef.current);
          timerIdRef.current = null;
        }
      };
    };
    t8 = [isRunning, stopTimer];
    $[11] = isRunning;
    $[12] = stopTimer;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== entry?.ended_at || $[16] !== handleSubmitAnswers || $[17] !== secondsLeft || $[18] !== stopTimer) {
    t9 = () => {
      if (secondsLeft !== 0 || stopTimer || isSubmittedRef.current) {
        return;
      }
      isSubmittedRef.current = true;
      const values = latestValuesRef.current;
      if (!values) {
        return;
      }
      setIsExpiredModalOpen(true);
      if (entry?.ended_at) {
        return;
      }
      handleSubmitAnswers?.({
        ...values,
        endTimestamp: dayjs().format("YYYY-MM-DD HH:mm:ss.SSS")
      });
      setIsExpiredModalOpen(true);
    };
    $[15] = entry?.ended_at;
    $[16] = handleSubmitAnswers;
    $[17] = secondsLeft;
    $[18] = stopTimer;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = entry?.ended_at;
  let t11;
  if ($[20] !== handleSubmitAnswers || $[21] !== secondsLeft || $[22] !== stopTimer || $[23] !== t10) {
    t11 = [secondsLeft, handleSubmitAnswers, t10, stopTimer];
    $[20] = handleSubmitAnswers;
    $[21] = secondsLeft;
    $[22] = stopTimer;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  useEffect(t9, t11);
  let t12;
  let t13;
  if ($[25] !== isPreview || $[26] !== secondsLeft) {
    t12 = () => {
      if (!isPreview || secondsLeft !== 0) {
        return;
      }
      setIsExpiredModalOpen(true);
    };
    t13 = [isPreview, secondsLeft];
    $[25] = isPreview;
    $[26] = secondsLeft;
    $[27] = t12;
    $[28] = t13;
  } else {
    t12 = $[27];
    t13 = $[28];
  }
  useEffect(t12, t13);
  let t14;
  if ($[29] !== entry || $[30] !== formik?.values || $[31] !== handleSubmitAnswers || $[32] !== initialSeconds || $[33] !== stopTimer) {
    t14 = () => {
      if (!entry || stopTimer) {
        return;
      }
      if (initialSeconds === 0 && !isSubmittedRef.current) {
        isSubmittedRef.current = true;
        setSecondsLeft(0);
        const values_0 = formik?.values ?? latestValuesRef.current;
        if (values_0) {
          handleSubmitAnswers?.({
            ...values_0,
            endTimestamp: dayjs().format("YYYY-MM-DD HH:mm:ss.SSS")
          });
        }
        setIsExpiredModalOpen(true);
      }
    };
    $[29] = entry;
    $[30] = formik?.values;
    $[31] = handleSubmitAnswers;
    $[32] = initialSeconds;
    $[33] = stopTimer;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = formik?.values;
  let t16;
  if ($[35] !== entry || $[36] !== handleSubmitAnswers || $[37] !== initialSeconds || $[38] !== stopTimer || $[39] !== t15) {
    t16 = [initialSeconds, entry, handleSubmitAnswers, t15, stopTimer];
    $[35] = entry;
    $[36] = handleSubmitAnswers;
    $[37] = initialSeconds;
    $[38] = stopTimer;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  useEffect(t14, t16);
  let t17;
  if ($[41] !== currentSurvey || $[42] !== entry || $[43] !== isExpiredModalOpen || $[44] !== isPreview || $[45] !== secondsLeft) {
    t17 = entry || isPreview ? <><div className="flex items-center gap-4"><p className="text-sm font-bold opacity-70">{formatSecondsToHHMMSS(secondsLeft)}</p><Divider orientation="vertical" className="h-4.5" /></div><ExpiredTimerModal currentSurvey={currentSurvey} isOpen={isExpiredModalOpen} setIsOpen={setIsExpiredModalOpen} entryId={entry?.id} /></> : null;
    $[41] = currentSurvey;
    $[42] = entry;
    $[43] = isExpiredModalOpen;
    $[44] = isPreview;
    $[45] = secondsLeft;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
};
function _temp(isoString) {
  const match = isoString.match(/(.*\.\d+)Z$/);
  if (!match) {
    return isoString;
  }
  const fractionalSeconds = match[1].split(".")[1] ?? "";
  const milliseconds = fractionalSeconds.slice(0, 3).padEnd(3, "0");
  return isoString.replace(/\.\d+Z$/, `.${milliseconds}Z`);
}
function _temp2(secondsLeft_0) {
  const totalSeconds_0 = Math.max(0, Math.floor(secondsLeft_0));
  const hours = Math.floor(totalSeconds_0 / 3600);
  const minutes = Math.floor(totalSeconds_0 % 3600 / 60);
  const seconds = totalSeconds_0 % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}